// File: webelieve-frontend/src/features/intake/components/PipelineMetricsBar.tsx
// M7.30: Pipeline metrics bar — now consuming the supervisory dashboard endpoint
// so the Pipeline view header is aligned with the same truth layer as EdgeFlow Monitor.

import React, { useState, useEffect } from 'react';
import { edgeflowApi, type DashboardMetrics } from '../api/edgeflowApi';

interface PipelineMetricsBarProps {
  refreshKey?: number;
}

const STAGE_COLORS: Record<string, string> = {
  new_lead: 'bg-blue-500',
  lead: 'bg-blue-400',
  contacted: 'bg-indigo-500',
  qualifying: 'bg-purple-500',
  qualified: 'bg-green-500',
  converted: 'bg-emerald-600',
  declined: 'bg-gray-400',
};

const STAGE_LABELS: Record<string, string> = {
  new_lead: 'New', lead: 'Lead', contacted: 'Contacted',
  qualifying: 'Qualifying', qualified: 'Qualified',
  converted: 'Converted', declined: 'Declined',
};

const PipelineMetricsBar: React.FC<PipelineMetricsBarProps> = ({ refreshKey = 0 }) => {
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const result = await edgeflowApi.getDashboardData();
        if (!cancelled && result.dashboardMetrics) setData(result.dashboardMetrics);
      } catch (err) {
        console.error('[PipelineMetricsBar] Failed to load metrics:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading || !data) {
    return (
      <div className="flex items-center gap-6 px-4 py-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        ))}
      </div>
    );
  }

  const { summary, supervisory } = data;
  const attentionCount = supervisory.stuck_leads_count + supervisory.stale_leads_count
    + supervisory.missing_conflict_check_count + supervisory.unresolved_workflow_failures_count;

  const statCards = [
    { label: 'Active Leads', value: supervisory.total_active_leads, color: 'text-lex-accent' },
    { label: 'Converted', value: summary.totalConverted, color: 'text-lex-success' },
    { label: 'Conversion', value: `${summary.conversionRate}%`, color: 'text-lex-accent' },
    {
      label: 'Needs Attention',
      value: attentionCount,
      color: attentionCount > 0 ? 'text-lex-warning' : 'text-lex-success',
    },
  ];

  // Stage distribution from supervisory truth
  const stages = Object.entries(data.byStage)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);
  const maxCount = Math.max(...stages.map(([, c]) => c), 1);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      {/* Stat cards */}
      <div className="flex items-center gap-6 mb-4">
        {statCards.map(card => (
          <div key={card.label} className="flex-1 text-center">
            <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Bottleneck indicator */}
      {supervisory.stage_bottleneck && (
        <div className="mb-3 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-xs text-amber-800 dark:text-amber-300">
          Bottleneck: <span className="font-semibold">{STAGE_LABELS[supervisory.stage_bottleneck] || supervisory.stage_bottleneck}</span> ({supervisory.stage_bottleneck_count} leads)
        </div>
      )}

      {/* Pipeline funnel from supervisory stage distribution */}
      <div className="space-y-1.5">
        {stages.map(([stage, count]) => (
          <div key={stage} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-20 text-right truncate">
              {STAGE_LABELS[stage] || stage}
            </span>
            <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${STAGE_COLORS[stage] || 'bg-gray-500'} rounded-full transition-all duration-500 ${stage === supervisory.stage_bottleneck ? 'ring-1 ring-amber-400' : ''}`}
                style={{ width: `${(count / maxCount) * 100}%`, minWidth: count > 0 ? '8px' : '0px' }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-8">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PipelineMetricsBar;
